import { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "../../hooks/useTheme.ts";
import type { Theme, VisionMode } from "../../providers/themeProvider.tsx";
import styles from "./themeSwitcher.module.scss";

const themeOptions: { value: Theme; label: string; icon: string }[] = [
  { value: "light", label: "Light", icon: "\u2600" },     // ☀
  { value: "dark", label: "Dark", icon: "\u263E" },       // ☾
  { value: "system", label: "System", icon: "\u2699" },   // ⚙
];

const visionOptions: { value: VisionMode; label: string }[] = [
  { value: "normal", label: "Normal vision" },
  { value: "protanopia", label: "Protanopia (red-blind)" },
  { value: "deuteranopia", label: "Deuteranopia (green-blind)" },
  { value: "tritanopia", label: "Tritanopia (blue-blind)" },
];

export function ThemeSwitcher() {
  const { theme, setTheme, visionMode, setVisionMode } = useTheme();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggle = useCallback(() => setOpen((o) => !o), []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className={styles.container}>
      <div className={styles.wrapper} role="radiogroup" aria-label="Theme">
        {themeOptions.map((opt) => (
          <button
            key={opt.value}
            className={`${styles.option} ${theme === opt.value ? styles.active : ""}`}
            onClick={() => setTheme(opt.value)}
            role="radio"
            aria-checked={theme === opt.value}
            aria-label={opt.label}
            title={opt.label}
          >
            {opt.icon}
          </button>
        ))}
      </div>

      <div className={styles.visionWrap} ref={menuRef}>
        <button
          className={`${styles.visionBtn} ${visionMode !== "normal" ? styles.visionActive : ""}`}
          onClick={toggle}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Color vision mode"
          title="Color vision mode"
        >
          {/* Simple eye icon via Unicode */}
          &#x25C9;
        </button>

        {open && (
          <div className={styles.visionMenu} role="menu">
            {visionOptions.map((opt) => (
              <button
                key={opt.value}
                className={`${styles.visionMenuItem} ${visionMode === opt.value ? styles.visionMenuActive : ""}`}
                role="menuitemradio"
                aria-checked={visionMode === opt.value}
                onClick={() => { setVisionMode(opt.value); setOpen(false); }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
